<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-900">
    <header class="bg-emerald-600 text-white py-6 px-4">
      <div class="max-w-3xl mx-auto">
        <a :href="backUrl" class="text-emerald-200 hover:text-white text-sm">&larr; {{ backLabel }}</a>
        <h1 class="text-2xl font-bold mt-1">{{ headerTitle }}</h1>
      </div>
    </header>

    <main class="max-w-3xl mx-auto px-4 py-6">
      <!-- Resume: an unfinished session was found for this context -->
      <div
        v-if="resumeAvailable && !exercises.length && !generating"
        class="mb-6 bg-white border-2 border-emerald-500 rounded-xl p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 dark:bg-gray-800"
      >
        <div>
          <p class="font-semibold text-gray-900 dark:text-gray-50">Pick up where you left off?</p>
          <p class="text-sm text-gray-500 mt-0.5 dark:text-gray-400">You were {{ savedProgressLabel }} through an unfinished session.</p>
        </div>
        <div class="flex gap-2 shrink-0">
          <button @click="resumeSession" class="bg-emerald-600 text-white px-5 py-2.5 rounded-xl font-semibold hover:bg-emerald-700 transition-colors">
            Resume
          </button>
          <button @click="discardResume" class="bg-white text-gray-600 border border-gray-300 px-5 py-2.5 rounded-xl font-medium hover:bg-gray-50 transition-colors dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700">
            Start over
          </button>
        </div>
      </div>

      <!-- Exercise Type Selection (demo/mistakes skip straight to a set) -->
      <div v-if="showTypePicker" class="space-y-6">
        <!-- Featured: Mixed -->
        <button
          @click="startExercises('mixed')"
          class="w-full bg-white border-2 border-emerald-500 rounded-xl p-5 text-left hover:bg-emerald-50 hover:shadow-sm transition-all dark:bg-gray-800"
        >
          <div class="flex items-center gap-3">
            <div class="text-3xl">🎲</div>
            <div>
              <h3 class="font-semibold text-gray-900 dark:text-gray-50">Mixed <span class="text-emerald-600 text-sm font-medium">· recommended</span></h3>
              <p class="text-sm text-gray-600 mt-0.5 dark:text-gray-300">A random mix of all exercise types</p>
            </div>
          </div>
        </button>

        <!-- Pro: Speaking -->
        <button
          v-if="showSpeaking"
          @click="chooseSpeaking"
          class="w-full bg-white border border-gray-200 rounded-xl p-5 text-left hover:border-emerald-300 hover:shadow-sm transition-all dark:bg-gray-800 dark:border-gray-700"
        >
          <div class="flex items-center justify-between gap-3">
            <div class="flex items-center gap-3">
              <div class="text-2xl">🎤</div>
              <div>
                <h3 class="font-semibold text-gray-900 dark:text-gray-50">Speaking</h3>
                <p class="text-sm text-gray-500 mt-1 dark:text-gray-400">Practice pronunciation by speaking aloud</p>
              </div>
            </div>
            <span
              v-if="speakingFree"
              class="shrink-0 text-xs font-semibold text-emerald-700 bg-emerald-100 rounded-full px-2.5 py-1"
            >Free</span>
            <span
              v-else-if="speakingLocked"
              class="shrink-0 text-xs font-semibold text-amber-700 bg-amber-100 rounded-full px-2.5 py-1"
            >🔒 Pro</span>
          </div>
        </button>

        <!-- Pick a specific type -->
        <div>
          <h2 class="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3 dark:text-gray-400">Pick a type</h2>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              v-for="type in standardTypes"
              :key="type.value"
              @click="startExercises(type.value)"
              class="bg-white border border-gray-200 rounded-xl p-5 text-left hover:border-emerald-300 hover:shadow-sm transition-all dark:bg-gray-800 dark:border-gray-700"
            >
              <div class="text-2xl mb-2">{{ type.icon }}</div>
              <h3 class="font-semibold text-gray-900 dark:text-gray-50">{{ type.label }}</h3>
              <p class="text-sm text-gray-500 mt-1 dark:text-gray-400">{{ type.description }}</p>
            </button>
          </div>
        </div>
      </div>

      <!-- Loading -->
      <div v-else-if="generating" class="text-center py-16">
        <div class="inline-block animate-spin rounded-full h-8 w-8 border-4 border-emerald-200 border-t-emerald-600"></div>
        <p class="mt-4 text-gray-500 dark:text-gray-400">Generating exercises...</p>
      </div>

      <!-- Error -->
      <div v-else-if="error" class="text-center py-12">
        <p class="text-red-500 mb-4">{{ error }}</p>
        <button @click="exerciseType = null" class="text-emerald-600 hover:text-emerald-700 font-medium">Try again</button>
      </div>

      <!-- Mistakes bank is empty -->
      <div v-else-if="mistakesEmpty" class="text-center py-16">
        <p class="text-5xl mb-4">🎉</p>
        <h2 class="text-xl font-bold text-gray-900 dark:text-gray-50">No mistakes to fix</h2>
        <p class="text-gray-500 mt-2 dark:text-gray-400">Anything you answer wrong in practice lands here to be cleared.</p>
        <a :href="backUrl" class="inline-block mt-6 bg-emerald-600 text-white px-6 py-3 rounded-xl font-semibold shadow-sm hover:bg-emerald-700 transition-colors">
          Back to Course
        </a>
      </div>

      <!-- Exercise Runner -->
      <div v-else-if="exercises.length && !sessionComplete">
        <div class="bg-white rounded-3xl border border-gray-200 shadow-sm p-6 sm:p-8 dark:bg-gray-800 dark:border-gray-700">
          <!-- Card header: context label + progress counter + track -->
          <div class="flex items-center justify-between text-xs font-medium text-gray-400 dark:text-gray-500">
            <span>
              {{ currentTypeLabel }}
              <span v-if="currentExercise.isRetry" class="ml-1 text-amber-500 font-semibold">· Retry</span>
            </span>
            <div class="flex items-center gap-2">
              <AutoplayToggle />
              <span>{{ currentIndex + 1 }} / {{ exercises.length }}</span>
            </div>
          </div>
          <div class="mt-2 mb-6 h-1.5 w-full rounded-full bg-gray-100 dark:bg-gray-800">
            <div
              class="h-1.5 rounded-full bg-emerald-500 transition-all duration-300"
              :style="{ width: `${(currentIndex / exercises.length) * 100}%` }"
            ></div>
          </div>

          <!-- Listening exercise: audio-only prompt -->
          <template v-if="renderType === 'listening' || (renderType === 'tone_drill' && !currentExercise.audio_options)">
            <p class="text-sm text-gray-500 mb-2 dark:text-gray-400">{{ currentExercise.instruction }}</p>
            <div class="flex flex-col items-center mb-6">
              <AudioButton
                ref="listeningAudio"
                v-if="currentExercise.audio_text"
                :src="currentExercise.audio_url"
                :text="currentExercise.audio_text"
                :show-slow="true"
                class="!w-16 !h-16 !text-2xl"
              />
              <p class="text-sm text-gray-400 mt-2 dark:text-gray-500">Tap to play</p>
            </div>
          </template>

          <!-- Tone drill: Match the Tone (audio_options) -->
          <template v-else-if="renderType === 'tone_drill' && currentExercise.audio_options">
            <p class="text-sm text-gray-500 mb-2 dark:text-gray-400">{{ currentExercise.instruction }}</p>
            <h2 class="text-2xl font-bold text-gray-900 text-center mb-6 dark:text-gray-50">{{ currentExercise.prompt }}</h2>
            <!-- Card is a div, not a button: nesting the AudioButton inside a
                 :disabled button is invalid HTML and made the options unplayable
                 once answered — exactly when you most want to hear the contrast.
                 Play and pick are now separate targets, and play always works. -->
            <div class="grid grid-cols-2 gap-3 mb-4">
              <div
                v-for="(opt, i) in currentExercise.audio_options"
                :key="i"
                :class="[
                  'flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-colors',
                  !answered ? 'border-gray-200' :
                  opt === currentExercise.correct_answer ? 'border-green-500 bg-green-50' :
                  opt === selectedAnswer && opt !== currentExercise.correct_answer ? 'border-red-500 bg-red-50' :
                  'border-gray-200 opacity-60'
                ]"
              >
                <AudioButton
                  :src="currentExercise.audio_option_urls?.[i]"
                  :text="opt"
                  :show-slow="true"
                  class="!w-12 !h-12"
                />
                <button
                  @click="submitAnswer(opt)"
                  :disabled="answered"
                  class="text-sm font-medium px-3 py-1 rounded-lg border transition-colors w-full disabled:cursor-default"
                  :class="!answered
                    ? 'border-gray-200 text-gray-700 hover:border-emerald-400 hover:bg-emerald-50'
                    : 'border-transparent text-gray-500'"
                >
                  <span v-if="answered && opt === currentExercise.correct_answer" class="text-emerald-600">&#10003; </span>
                  <span v-else-if="answered && opt === selectedAnswer" class="text-red-500">&#10007; </span>
                  {{ answered ? String.fromCharCode(65 + i) : `Pick ${String.fromCharCode(65 + i)}` }}
                </button>
              </div>
            </div>
          </template>

          <!-- Speaking exercise -->
          <template v-else-if="renderType === 'speaking'">
            <p class="text-sm text-gray-500 mb-2 dark:text-gray-400">{{ currentExercise.instruction }}</p>
            <h2 class="text-xl font-semibold text-gray-900 mb-2 dark:text-gray-50">{{ currentExercise.prompt }}</h2>
            <div class="flex items-center gap-2 mb-4">
              <span class="text-sm text-gray-500 dark:text-gray-400">Reference:</span>
              <AudioButton ref="speakingAudio" v-if="currentExercise.audio_text" :src="currentExercise.audio_url" :text="currentExercise.audio_text" :show-slow="true" />
            </div>
            <SpeechRecorder
              :key="`speak-${currentIndex}`"
              v-if="!answered"
              :expected-text="currentExercise.correct_answer"
              :reference-audio-text="currentExercise.audio_text"
              :reference-audio-url="currentExercise.audio_url"
              :level="level"
              @result="handleSpeakingResult"
            />
          </template>

          <!-- All other exercises: text prompt -->
          <template v-else>
            <p class="text-sm text-gray-500 mb-2 dark:text-gray-400">{{ currentExercise.instruction }}</p>
            <div class="flex items-center gap-2 mb-2">
              <h2 class="text-2xl font-bold text-gray-900 dark:text-gray-50">{{ currentExercise.prompt }}</h2>
              <AudioButton
                ref="promptAudio"
                v-if="currentExercise.audio_text"
                :src="currentExercise.audio_url"
                :text="currentExercise.audio_text"
              />
            </div>
            <p v-if="currentExercise.english_context" class="text-sm text-gray-400 italic mb-6 dark:text-gray-500">
              {{ currentExercise.english_context }}
            </p>
            <div v-else class="mb-4"></div>
          </template>

          <!-- Multiple Choice / Listening / Tone Drill Listen&Identify (all use option buttons) -->
          <!-- Audio-option tone drills answer via the A/B/C/D buttons in the
               prompt area above, so they must not also fall through to the
               free-text branch. They carry `audio_options` and no `options`
               (exercise.compact strips the nil), which sent 190 of 559 drills
               into the `v-else` and rendered a "Type your answer…" box beneath
               the audio buttons — typing there submitted a wrong answer. -->
          <div v-if="renderType === 'tone_drill' && currentExercise.audio_options"></div>
          <div v-else-if="renderType === 'multiple_choice' || renderType === 'listening' || (renderType === 'tone_drill' && currentExercise.options)" class="space-y-2.5">
            <button
              v-for="(option, i) in currentExercise.options"
              :key="i"
              @click="submitAnswer(option.text)"
              :disabled="answered"
              :class="[
                'w-full flex items-center justify-between gap-3 text-left font-medium px-4 py-3.5 rounded-xl border-2 transition-all',
                !answered ? 'border-gray-200 dark:border-gray-600 text-gray-800 dark:text-gray-100 hover:border-emerald-400 hover:bg-emerald-50 dark:hover:bg-gray-700' :
                option.is_correct ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950 text-gray-900 dark:text-emerald-100' :
                option.text === selectedAnswer && !option.is_correct ? 'border-red-500 bg-red-50 dark:bg-red-950 text-gray-900 dark:text-red-100' :
                'border-gray-200 dark:border-gray-600 text-gray-800 dark:text-gray-100 opacity-50'
              ]"
            >
              <span><span class="hidden sm:inline text-xs text-gray-300 mr-2">{{ i + 1 }}</span>{{ option.text }}</span>
              <span v-if="answered && option.is_correct" class="text-emerald-600">&#10003;</span>
              <span v-else-if="answered && option.text === selectedAnswer" class="text-red-500">&#10007;</span>
            </button>
          </div>

          <!-- Speaking: SpeechRecorder above is the answer UI; just offer a skip -->
          <div v-else-if="renderType === 'speaking'">
            <button
              v-if="!answered"
              @click="skipSpeaking"
              class="bg-white text-gray-600 border border-gray-300 px-6 py-2 rounded-lg font-medium hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700"
            >
              Skip speaking
            </button>
          </div>

          <!-- Sentence Building: word bank tiles -->
          <div v-else-if="renderType === 'sentence_building'">
            <!-- Answer zone -->
            <div class="min-h-[52px] border-2 border-dashed border-gray-300 rounded-lg p-3 mb-3 flex flex-wrap gap-2 dark:border-gray-600">
              <span v-if="!selectedWords.length && !answered" class="text-sm text-gray-400 self-center dark:text-gray-500">
                Tap words below to build your sentence
              </span>
              <button
                v-for="(word, i) in selectedWords"
                :key="'sel-' + i"
                @click="deselectWord(i)"
                :disabled="answered"
                class="px-3 py-2 rounded-lg border-2 border-emerald-500 bg-emerald-50 text-emerald-800 font-medium transition-colors"
                :class="answered ? 'opacity-60 cursor-not-allowed' : 'hover:bg-emerald-100 cursor-pointer'"
              >
                {{ word }}
              </button>
            </div>
            <!-- Word bank -->
            <div class="flex flex-wrap gap-2 mb-3">
              <button
                v-for="(word, i) in availableWords"
                :key="'avail-' + i"
                @click="selectWord(word, i)"
                :disabled="answered"
                class="px-3 py-2 rounded-lg border-2 border-gray-200 bg-white font-medium transition-colors dark:bg-gray-800 dark:border-gray-700 text-gray-900 dark:text-gray-100"
                :class="answered ? 'opacity-60 cursor-not-allowed' : 'hover:border-emerald-300 hover:bg-emerald-50 cursor-pointer'"
              >
                {{ word }}
              </button>
            </div>
            <button
              v-if="!answered"
              @click="submitAnswer(selectedWords.join(' '))"
              :disabled="placedWordCount < expectedWordCount"
              class="mt-1 bg-emerald-600 text-white px-6 py-3 rounded-xl font-semibold shadow-sm hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Check
            </button>
          </div>

          <!-- Fill Blank / Translation -->
          <div v-else>
            <VietnameseInput
              v-model="textAnswer"
              @enter="submitAnswer(textAnswer)"
              :disabled="answered"
              class="w-full border-2 border-gray-200 rounded-xl px-4 py-3.5 text-lg focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100 dark:border-gray-700"
              placeholder="Type your answer..."
            />
            <button
              v-if="!answered"
              @click="submitAnswer(textAnswer)"
              :disabled="!textAnswer.trim()"
              class="mt-3 bg-emerald-600 text-white px-6 py-3 rounded-xl font-semibold shadow-sm hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Check
            </button>
          </div>

          <!-- Hint -->
          <div v-if="currentExercise.hint && !answered" class="mt-3">
            <button
              v-if="!hintShown"
              @click="hintShown = true"
              class="text-sm text-amber-600 hover:text-amber-700 underline"
            >
              Show hint
            </button>
            <p v-else class="text-sm text-amber-700 bg-amber-50 rounded-lg p-3">
              {{ currentExercise.hint }}
            </p>
          </div>

          <!-- Feedback -->
          <div v-if="answered" class="mt-6 p-4 rounded-xl" :class="isCorrect ? 'bg-emerald-50 dark:bg-emerald-950' : 'bg-red-50 dark:bg-red-950'">
            <p class="font-semibold flex items-center gap-2" :class="isCorrect ? 'text-emerald-700 dark:text-emerald-300' : 'text-red-700 dark:text-red-300'">
              {{ isCorrect ? 'Correct!' : 'Not quite' }}
              <!-- No XP chip in the demo (nothing recorded) or on retries (only
                   first attempts earn XP). Retries get a "cleared" nod instead. -->
              <span v-if="!demoMode && !currentExercise.isRetry" class="text-xs font-bold rounded-lg px-2 py-0.5 animate-bounce"
                    :class="isCorrect ? 'text-emerald-700 bg-emerald-100' : 'text-amber-700 bg-amber-100'">
                +{{ isCorrect ? 10 : 5 }} XP
              </span>
              <span v-else-if="currentExercise.isRetry && isCorrect" class="text-xs font-bold rounded-lg px-2 py-0.5 text-emerald-700 bg-emerald-100">
                Cleared ✓
              </span>
            </p>
            <p v-if="!isCorrect" class="text-sm text-gray-600 mt-1 dark:text-gray-300">
              Answer:
              <TappableText
                v-if="glossAnswer"
                :key="`gloss-wrong-${currentExercise.id}`"
                class="font-medium"
                :text="currentExercise.correct_answer"
                :source="{ kind: 'exercise', id: currentExercise.id }"
              />
              <span v-else class="font-medium">{{ currentExercise.correct_answer }}</span>
            </p>
            <p v-if="!isCorrect && !demoMode && !currentExercise.isRetry" class="text-xs text-amber-700 mt-1">
              This one will come back around before the end.
            </p>
            <p v-if="isCorrect && toneMismatch" class="text-sm text-amber-700 mt-1">
              Watch your tones &rarr; <span class="font-medium">{{ currentExercise.correct_answer }}</span>
            </p>
            <!-- A listening or multiple-choice item can be passed by matching a
                 single syllable, and a correct answer is never shown again — so
                 the learner never finds out what they just heard. The gloss is
                 post-answer only: showing it above would hand over options that
                 differ by one word. -->
            <p v-if="isCorrect && glossAnswer" class="text-sm text-gray-600 mt-1 dark:text-gray-300">
              <TappableText
                :key="`gloss-right-${currentExercise.id}`"
                class="font-medium"
                :text="currentExercise.correct_answer"
                :source="{ kind: 'exercise', id: currentExercise.id }"
              />
              <span class="text-gray-400 dark:text-gray-500"> &mdash; tap a word for its meaning</span>
            </p>
            <p class="text-sm text-gray-600 mt-1 dark:text-gray-300">{{ currentExercise.explanation }}</p>
            <button
              @click="nextExercise"
              class="mt-3 bg-emerald-600 text-white px-6 py-3 rounded-xl font-semibold shadow-sm hover:bg-emerald-700 transition-colors"
            >
              {{ currentIndex < exercises.length - 1 ? 'Next' : 'See Results' }}
            </button>
            <p class="hidden sm:block text-xs text-gray-300 mt-2">Enter for next · 1–4 to answer</p>
          </div>

          <!-- Report a problem: low-emphasis link in the card footer -->
          <div class="mt-6 pt-4 border-t border-gray-100 text-right dark:border-gray-700">
            <span v-if="currentReported" class="text-xs text-gray-400 dark:text-gray-500">Thanks for the report</span>
            <button
              v-else
              type="button"
              @click="showReport = true"
              class="text-xs text-gray-400 hover:text-gray-600 inline-flex items-center gap-1 dark:text-gray-500"
            >
              <span aria-hidden="true">⚐</span> Report a problem
            </button>
          </div>
        </div>
      </div>

      <ReportProblemModal
        v-if="showReport"
        :exercise="currentExercise"
        :unit-id="unitId"
        :level="level"
        :review-mode="reviewMode"
        :demo-mode="demoMode"
        :submitted-answer="givenAnswer"
        @submitted="onReportSent"
        @close="showReport = false"
      />

      <!-- Session Summary -->
      <div v-if="sessionComplete" class="text-center py-12">
        <p v-if="saveWarning"
           class="mb-6 mx-auto max-w-sm rounded-xl bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 px-4 py-3 text-sm text-amber-900 dark:text-amber-200">
          We couldn't save this session &mdash; your XP and progress for it weren't recorded.
          Check your connection and try the unit again.
        </p>
        <!-- Unit completion: the moment `completed` flips true gets a real celebration. -->
        <template v-if="completedNow">
          <div class="text-6xl mb-2">🏆</div>
          <div class="text-2xl mb-3 tracking-widest">🎉✨🎉</div>
          <h2 class="text-3xl font-bold text-emerald-600">Unit complete!</h2>
          <p class="text-lg text-gray-600 dark:text-gray-300 mt-2">
            {{ score }} of {{ totalAnswered }} correct — this unit is done.
          </p>
        </template>

        <!-- Level checkpoint results -->
        <template v-else-if="checkpointResult">
          <template v-if="checkpointResult.passed_now">
            <div class="text-6xl mb-2">🏅</div>
            <h2 class="text-3xl font-bold text-emerald-600">Checkpoint passed!</h2>
            <p class="text-lg text-gray-600 dark:text-gray-300 mt-2">
              {{ sessionResult.session_score }}% on the Level {{ level }} checkpoint.
              All Level {{ level }} units are now marked learned.
            </p>
          </template>
          <template v-else-if="checkpointResult.passed">
            <div class="text-6xl mb-4">🏅</div>
            <h2 class="text-2xl font-bold text-gray-900 dark:text-gray-50">Session Complete</h2>
            <p class="text-lg text-gray-600 dark:text-gray-300 mt-2">
              {{ score }} of {{ totalAnswered }} correct — checkpoint already passed (best {{ checkpointResult.best_pct }}%)
            </p>
          </template>
          <template v-else>
            <div class="text-6xl mb-4">💪</div>
            <h2 class="text-2xl font-bold text-gray-900 dark:text-gray-50">Not this time</h2>
            <p class="text-lg text-gray-600 dark:text-gray-300 mt-2">
              {{ sessionResult.session_score }}% — score {{ checkpointResult.pass_pct }}%+ to pass this checkpoint.
              Your misses are saved below and in your mistakes bank.
            </p>
          </template>
        </template>

        <template v-else>
          <div class="text-6xl mb-4">{{ score >= totalAnswered * 0.7 ? '🎉' : '💪' }}</div>
          <h2 class="text-2xl font-bold text-gray-900 dark:text-gray-50">Session Complete</h2>
          <p class="text-lg text-gray-600 dark:text-gray-300 mt-2">
            <template v-if="mistakesMode">
              You cleared <span class="font-bold text-emerald-600">{{ score }}</span> of
              <span class="font-bold">{{ totalAnswered }}</span> mistakes
            </template>
            <template v-else>
              You got <span class="font-bold text-emerald-600">{{ score }}</span> out of
              <span class="font-bold">{{ totalAnswered }}</span> correct
            </template>
          </p>
          <p v-if="showCompletionHint" class="text-sm text-gray-500 mt-1 dark:text-gray-400">
            Score 70%+ in a session to complete this unit.
          </p>
        </template>

        <div v-if="sessionResult?.stats" class="mt-4 space-y-1">
          <p class="text-sm text-gray-600 dark:text-gray-300">
            +{{ sessionResult.xp_earned }} XP earned
            <span v-if="sessionResult.bonus_xp > 0" class="text-emerald-600 font-medium">
              (includes +{{ sessionResult.bonus_xp }} bonus!)
            </span>
          </p>
          <p class="text-sm text-gray-600 dark:text-gray-300">
            &#128293; {{ sessionResult.stats.current_streak }} day streak
            &middot; Level {{ sessionResult.stats.level }}
          </p>
        </div>
        <!-- Demo: convert the session into a sign-up -->
        <div v-if="demoMode" class="mt-8 max-w-md mx-auto bg-emerald-50 border border-emerald-200 rounded-2xl p-6 text-center">
          <h3 class="text-lg font-bold text-gray-900 dark:text-gray-50">That's a taste of Unit 1 🎉</h3>
          <p class="mt-2 text-sm text-gray-600 dark:text-gray-300">
            Create a free account to save your progress and unlock all the units, games,
            spaced review, and conversation practice.
          </p>
          <a
            :href="registerUrl"
            @click="track('demo_signup_clicked', { score: score, total: exercises.length })"
            class="mt-4 inline-flex items-center justify-center font-semibold bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-xl shadow-sm transition-colors"
          >
            Create a free account
          </a>
        </div>

        <!-- Actions come BEFORE the mistake list. This is the highest-motivation
             moment in the product, and burying "Next unit" under the error
             cards put it well below the fold on a phone. -->
        <div class="mt-6 flex flex-col sm:flex-row gap-3 justify-center items-center">
          <!-- Forward routing: a completed unit points at the NEXT unit, not back at itself. -->
          <a
            v-if="showNextUnit"
            :href="nextUnitUrl"
            class="bg-emerald-600 text-white border border-emerald-600 px-6 py-3 rounded-xl font-semibold shadow-sm hover:bg-emerald-700 transition-colors"
          >
            {{ nextUnitLocked ? `🔒 Unlock Unit ${nextUnitPosition}` : `Next: Unit ${nextUnitPosition} · ${nextUnitTitle}` }} &rarr;
          </a>
          <button
            @click="resetSession"
            class="bg-white text-gray-700 border border-gray-300 px-6 py-2 rounded-lg font-medium hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-700"
          >
            {{ demoMode ? 'Try again' : (mistakesMode ? 'Fix more mistakes' : 'Practice Again') }}
          </button>
          <a
            :href="backUrl"
            class="bg-white text-gray-700 border border-gray-300 px-6 py-2 rounded-lg font-medium hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-700"
          >
            {{ backLabel }}
          </a>
        </div>

        <!-- Mistake Review -->
        <div v-if="mistakes.length" class="mt-8 text-left max-w-2xl mx-auto">
          <h3 class="font-semibold text-gray-900 mb-3 text-center dark:text-gray-50">Review your mistakes</h3>
          <div v-for="(m, i) in mistakes" :key="i" class="bg-white border border-gray-200 rounded-lg p-4 mb-2 dark:bg-gray-800 dark:border-gray-700">
            <p class="text-sm font-medium text-gray-900 dark:text-gray-50">{{ m.prompt }}</p>
            <p class="text-sm text-red-600 mt-1">Your answer: <span class="font-medium">{{ m.given_answer }}</span></p>
            <p class="text-sm text-green-700 mt-1">Correct: <span class="font-medium">{{ m.correct_answer }}</span></p>
            <p v-if="m.explanation" class="text-sm text-gray-500 mt-1 dark:text-gray-400">{{ m.explanation }}</p>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { showUpsell } from '../utils/upsell'
import { apiGet, apiPost } from '../api/client'
import AudioButton from './shared/AudioButton.vue'
import AutoplayToggle from './shared/AutoplayToggle.vue'
import SpeechRecorder from './shared/SpeechRecorder.vue'
import VietnameseInput from './shared/VietnameseInput.vue'
import ReportProblemModal from './shared/ReportProblemModal.vue'
import TappableText from './shared/TappableText.vue'
import { prefetchStaticAudio } from '../utils/ttsCache'
import { autoplayEnabled } from '../utils/autoplayPref'
import { checkVietnameseAnswer } from '../utils/similarity'
import { sessionKey, saveSession, loadSession, clearSession } from '../utils/sessionStore'
import { track } from '../utils/analytics'

const props = defineProps({
  unitId: { type: Number, default: null },
  exerciseUrl: { type: String, required: true },
  unitUrl: { type: String, default: null },
  backUrl: { type: String, required: true },
  level: { type: Number, default: null },
  reviewMode: { type: Boolean, default: false },
  // Mistakes mode: replays exercises from the learner's mistakes bank
  // (GET exerciseUrl) until answered correctly; recording clears them.
  mistakesMode: { type: Boolean, default: false },
  // Optional whitelist of exercise-pool types this unit actually has content for.
  // When provided, the type grid is limited to these (plus the always-live 'speaking'
  // and 'mixed'), so units like Alphabet don't advertise types with no exercises.
  availableTypes: { type: Array, default: null },
  // Demo mode: public, no-account taste of the product. Auto-starts a short
  // mixed set, never records progress, and ends on a sign-up call to action.
  demoMode: { type: Boolean, default: false },
  registerUrl: { type: String, default: null },
  unitTitle: { type: String, default: null },
  // Whether the current user has an active Pro subscription. Drives the lock
  // badge on the Speaking card so the paywall is shown before recording.
  subscribed: { type: Boolean, default: false },
  speakingFree: { type: Boolean, default: false },
  // The next unit in course order, for forward routing off the summary screen.
  nextUnitUrl: { type: String, default: null },
  nextUnitTitle: { type: String, default: null },
  nextUnitPosition: { type: Number, default: null },
  nextUnitLocked: { type: Boolean, default: false },
})

const allExerciseTypes = [
  { value: 'multiple_choice', label: 'Multiple Choice', icon: '🔘', description: 'Pick the correct answer from 4 options' },
  { value: 'fill_blank', label: 'Fill in the Blank', icon: '✏️', description: 'Complete the sentence with the right word' },
  { value: 'translation', label: 'Translation', icon: '🔄', description: 'Translate between Vietnamese and English' },
  { value: 'sentence_building', label: 'Sentence Building', icon: '🧩', description: 'Arrange words into correct order' },
  { value: 'listening', label: 'Listening', icon: '🎧', description: 'Listen to audio and pick the sentence you heard' },
  { value: 'mixed', label: 'Mixed', icon: '🎲', description: 'A random mix of all exercise types' },
  { value: 'grammar_focus', label: 'Grammar Focus', icon: '📐', description: 'Practice grammar patterns and sentence structure' },
  { value: 'error_correction', label: 'Error Correction', icon: '🔍', description: 'Find and fix the mistake in the sentence', minLevel: 3 },
  { value: 'tone_drill', label: 'Tone Drills', icon: '🎵', description: 'Practice distinguishing Vietnamese tones' },
  { value: 'speaking', label: 'Speaking', icon: '🎤', description: 'Practice pronunciation by speaking aloud' },
]

const effectiveLevel = computed(() => (props.level == null ? 1 : props.level))

const headerTitle = computed(() => {
  if (props.demoMode) return props.unitTitle ? `Free demo · ${props.unitTitle}` : 'Free demo lesson'
  if (props.mistakesMode) return 'Fix your mistakes'
  if (props.reviewMode) return `Level ${props.level} Checkpoint`
  return 'Practice'
})

const backLabel = computed(() => {
  if (props.demoMode) return 'Back to home'
  if (props.reviewMode || props.mistakesMode) return 'Back to Course'
  return 'Back to Lesson'
})

// Standard, directly-pickable types — excludes 'mixed' (featured separately) and
// 'speaking' (its own Pro section). Honors availableTypes + per-type minLevel.
const standardTypes = computed(() => {
  return allExerciseTypes.filter(t => {
    if (t.value === 'mixed' || t.value === 'speaking') return false
    if (t.minLevel && effectiveLevel.value < t.minLevel) return false
    if (!props.availableTypes) return true
    return props.availableTypes.includes(t.value)
  })
})

// Speaking is pre-generated per unit only (no review pool), free on Level 1, and
// Pro elsewhere. Show the lock before recording rather than 402-ing on submit.
// Only surface it where the unit actually has speaking content — e.g. the
// Alphabet & Tones unit has none, so we don't offer (and live-fallback) it there.
const showSpeaking = computed(() =>
  !props.reviewMode && (props.availableTypes == null || props.availableTypes.includes("speaking"))
)
// Server-computed (Unit.free_through_level?) so the badge can never claim
// something the API would refuse, or hide something it would allow.
const speakingFree = computed(() => props.speakingFree)
const speakingLocked = computed(() => !props.subscribed && !speakingFree.value)

function chooseSpeaking() {
  if (speakingLocked.value) {
    showUpsell({ level: effectiveLevel.value })
    return
  }
  startExercises('speaking')
}

const exerciseType = ref(null)
const exercises = ref([])
const currentIndex = ref(0)
const generating = ref(false)
const error = ref(null)
const answered = ref(false)
const selectedAnswer = ref('')
const textAnswer = ref('')
const isCorrect = ref(false)
const toneMismatch = ref(false)
// Strict unless the user explicitly turned it off in Settings.
const strictTones = (() => {
  try { return localStorage.getItem('speaksaigon_strict_tones') !== 'false' } catch (e) { return true }
})()
// Lenient tone matching only applies to free-text answers; selection-based
// types (multiple choice, listening, etc.) must always match exactly so a
// tone-only-different distractor is never accepted.
const TYPED_TYPES = ['fill_blank', 'translation', 'grammar_focus', 'error_correction']
const score = ref(0)
const sessionComplete = ref(false)
const sessionAttempts = ref([])
const sessionResult = ref(null)
// Set when progress could not be saved, so the summary can say so rather than
// implying the session counted.
const saveWarning = ref(false)
const mistakesEmpty = ref(false)

// How many times a missed exercise gets re-queued before we let it go.
const MAX_REQUEUES = 2
// Re-queued misses reappear a few cards later, not immediately.
const REQUEUE_GAP = 3

const showTypePicker = computed(() =>
  !exerciseType.value && !props.demoMode && !props.mistakesMode && !mistakesEmpty.value
)

const currentExercise = computed(() => exercises.value[currentIndex.value] || {})
const renderType = computed(() => currentExercise.value.display_type || currentExercise.value.type)

// Whether to render the answer as tappable words in the feedback box. The
// server decides the language (answer_is_vietnamese) because glossing an
// English answer against Vietnamese vocabulary produces nonsense — "to" and
// "con" are both real course words. Speaking rows are excluded: the recorder
// already shows the target sentence with its own breakdown.
const glossAnswer = computed(() => (
  currentExercise.value.answer_is_vietnamese === true &&
  renderType.value !== 'speaking' &&
  !!currentExercise.value.correct_answer
))
const currentTypeLabel = computed(() => {
  // The learner's CHOICE wins over the render type. Grammar Focus rows are
  // stored as multiple_choice so they render with option buttons, which meant
  // picking "Grammar Focus" opened a session headed "Multiple Choice".
  // renderType is only the fallback, for Mixed and for the mistakes replay.
  const key = exerciseType.value || renderType.value
  const t = allExerciseTypes.find(t => t.value === key)
  return t ? t.label : 'Practice'
})
const listeningAudio = ref(null)
const promptAudio = ref(null)
const speakingAudio = ref(null)
const hintShown = ref(false)
const selectedWords = ref([])
// Guards the Check button so a single stray tap can't submit a one-word answer
// and burn the question. Counted in WORDS, not tiles: a tile can hold a phrase
// ("gia đình", "cảm ơn"), so comparing tile count against answer word count
// made Check unreachable — the learner was stuck with no way to submit.
const expectedWordCount = computed(() => {
  const answer = currentExercise.value?.correct_answer
  return answer ? String(answer).trim().split(/\s+/).length : 1
})
const placedWordCount = computed(() =>
  selectedWords.value.join(' ').trim().split(/\s+/).filter(Boolean).length
)
const availableWords = ref([])
const mistakes = computed(() => sessionAttempts.value.filter(a => !a.is_correct))
// First-attempt totals: retries affect neither the score nor the denominator.
const totalAnswered = computed(() => sessionAttempts.value.length)

const completedNow = computed(() => !!sessionResult.value?.completed_now)
const checkpointResult = computed(() => sessionResult.value?.checkpoint || null)
const unitCompleted = computed(() => !!sessionResult.value?.progress?.completed)
const showNextUnit = computed(() =>
  !props.demoMode && !props.reviewMode && !props.mistakesMode &&
  unitCompleted.value && !!props.nextUnitUrl
)
const showCompletionHint = computed(() =>
  !props.demoMode && !props.reviewMode && !props.mistakesMode &&
  !!sessionResult.value && !unitCompleted.value
)

// --- Session persistence: reload/back never destroys an in-progress session ---
const storageKey = sessionKey({
  demoMode: props.demoMode,
  reviewMode: props.reviewMode,
  mistakesMode: props.mistakesMode,
  level: props.level,
  unitId: props.unitId,
})
const resumeAvailable = ref(false)
let savedState = null

const savedProgressLabel = computed(() => {
  if (!savedState) return ''
  const total = savedState.exercises.length
  return `${Math.min(savedState.currentIndex + 1, total)} / ${total}`
})

function persist() {
  if (sessionComplete.value) return
  saveSession(storageKey, {
    exerciseType: exerciseType.value,
    exercises: exercises.value,
    currentIndex: currentIndex.value,
    score: score.value,
    sessionAttempts: sessionAttempts.value,
  })
}

function resumeSession() {
  if (!savedState) return
  exerciseType.value = savedState.exerciseType
  exercises.value = savedState.exercises
  currentIndex.value = savedState.currentIndex
  score.value = savedState.score || 0
  sessionAttempts.value = savedState.sessionAttempts || []
  resumeAvailable.value = false
  savedState = null

  const audioUrls = exercises.value.flatMap(ex => [
    ex.audio_url,
    ...(ex.audio_option_urls || []),
  ]).filter(Boolean)
  if (audioUrls.length) prefetchStaticAudio(audioUrls)
}

function discardResume() {
  clearSession(storageKey)
  resumeAvailable.value = false
  savedState = null
  if (props.mistakesMode) startExercises('mixed')
}

// "Report a problem" — track which cards have been reported so we can hide the
// link after submitting and avoid duplicate reports for the same exercise.
const showReport = ref(false)
const reportedIndexes = ref(new Set())
const currentReported = computed(() => reportedIndexes.value.has(currentIndex.value))
// Best-available learner answer to attach to the report, across the input types.
const givenAnswer = computed(() =>
  selectedAnswer.value || textAnswer.value || selectedWords.value.join(' ') || ''
)

function onReportSent() {
  // Mark this card as reported; the modal stays open on its thank-you view until
  // the learner dismisses it (which fires @close).
  reportedIndexes.value = new Set(reportedIndexes.value).add(currentIndex.value)
}

// Whether the Vietnamese audio on a *text-prompt* card is safe to auto-play.
// The text-prompt template covers multiple_choice / fill_blank / sentence_building /
// error_correction / translation (and grammar exercises rendered as those). For all
// of those the audio_text is (or reveals) the answer — EXCEPT a Vietnamese→English
// translation, where audio_text is the Vietnamese *prompt* being translated, not the
// answer. EN→VN translation sets audio_text = correct_answer, so comparing the two
// cleanly tells the two directions apart.
function shouldAutoplayPrompt(ex) {
  const t = ex.display_type || ex.type
  if (t !== 'translation') return false
  const norm = (s) => (s || '').trim().toLowerCase()
  return !!ex.audio_text && norm(ex.audio_text) !== norm(ex.correct_answer)
}

// Initialize word tiles or auto-play audio when exercise changes
watch(currentExercise, async (ex) => {
  // Autoplay is a true master switch: when off, nothing auto-plays (tap to play).
  if (ex.audio_text && autoplayEnabled.value) {
    await nextTick()
    // Try whichever audio ref is active for the current exercise template.
    if (listeningAudio.value) {
      // Listening / tone-drill: the audio IS the question.
      listeningAudio.value.play()
    } else if (speakingAudio.value) {
      // Speaking: the reference pronunciation to hear before repeating.
      speakingAudio.value.play()
    } else if (promptAudio.value && shouldAutoplayPrompt(ex)) {
      // Text-prompt card: only VN→EN translation (audio = prompt, not answer).
      promptAudio.value.play()
    }
  }
  if ((ex.display_type || ex.type) === 'sentence_building' && ex.prompt) {
    selectedWords.value = []
    // Split on bare commas and strip punctuation-only leftovers: some generated
    // prompts carry a token's trailing comma (e.g. "chào,, Xin"), which the old
    // split(', ') turned into a literal "chào," word tile.
    availableWords.value = ex.prompt
      .split(',')
      .map(w => w.trim())
      .filter(w => w && !/^[.,!?]+$/.test(w))
      .sort(() => Math.random() - 0.5)
  }
})

function selectWord(word, index) {
  if (answered.value) return
  selectedWords.value.push(word)
  availableWords.value.splice(index, 1)
}

function deselectWord(index) {
  if (answered.value) return
  const word = selectedWords.value.splice(index, 1)[0]
  availableWords.value.push(word)
}

async function startExercises(type) {
  exerciseType.value = type
  generating.value = true
  error.value = null

  try {
    let data
    if (props.mistakesMode) {
      data = await apiGet(`${props.exerciseUrl}?count=10`)
      if (!data.exercises?.length) {
        mistakesEmpty.value = true
        exerciseType.value = null
        return
      }
    } else {
      const body = props.reviewMode
        ? { level: props.level, exercise_type: type, count: 20 }
        : { unit_id: props.unitId, exercise_type: type, count: props.demoMode ? 6 : 10 }
      data = await apiPost(props.exerciseUrl, body)
    }
    exercises.value = data.exercises.map(ex => {
      if (ex.options) {
        ex.options = ex.options.sort(() => Math.random() - 0.5)
      }
      return ex
    })
    persist()

    // Prefetch all static audio URLs
    const audioUrls = exercises.value.flatMap(ex => [
      ex.audio_url,
      ...(ex.audio_option_urls || []),
    ]).filter(Boolean)
    if (audioUrls.length) prefetchStaticAudio(audioUrls)
  } catch (e) {
    error.value = "We couldn't build this practice set just now. Try again in a moment."
  } finally {
    generating.value = false
  }
}

// A serializable copy of the exercise, sent with each attempt so wrong answers
// can be replayed later (the mistakes bank). Retry bookkeeping stays local.
function exerciseSnapshot(ex) {
  const { isRetry, retryChain, ...clean } = ex
  return clean
}

// Missed exercises come back a few cards later until answered correctly (or
// re-queued MAX_REQUEUES times) — practice until you've got it, not a one-shot
// quiz. The demo stays a short fixed set.
function queueRetry(ex) {
  const chain = (ex.retryChain || 0) + 1
  if (chain > MAX_REQUEUES) return
  const clone = { ...ex, isRetry: true, retryChain: chain }
  const at = Math.min(currentIndex.value + REQUEUE_GAP, exercises.value.length)
  exercises.value.splice(at, 0, clone)
}

function submitAnswer(answer) {
  if (answered.value) return
  answered.value = true
  selectedAnswer.value = answer

  const correct = currentExercise.value.correct_answer
  const acceptables = currentExercise.value.acceptable_answers || []
  const typed = TYPED_TYPES.includes(renderType.value)

  const result = checkVietnameseAnswer(answer, correct, acceptables, {
    strict: strictTones || !typed,
  })
  isCorrect.value = result.isCorrect
  toneMismatch.value = result.toneMismatch

  // Only first attempts count toward score/XP; retries exist to re-teach.
  if (!currentExercise.value.isRetry) {
    if (isCorrect.value) score.value++
    sessionAttempts.value.push({
      prompt: currentExercise.value.prompt,
      given_answer: answer,
      correct_answer: correct,
      is_correct: isCorrect.value,
      explanation: currentExercise.value.explanation,
      exercise: exerciseSnapshot(currentExercise.value),
    })
  }

  if (!isCorrect.value && !props.demoMode && renderType.value !== 'speaking') {
    queueRetry(currentExercise.value)
  }
}

function handleSpeakingResult({ correct, recognizedText }) {
  submitAnswer(correct ? currentExercise.value.correct_answer : (recognizedText || '(not recognized)'))
}

function skipSpeaking() {
  if (answered.value) return
  // Advance without recording an attempt. Skipped exercises don't count
  // toward score, XP, or session_pct (record_session uses attempts.length).
  if (currentIndex.value < exercises.value.length - 1) {
    currentIndex.value++
    answered.value = false
    selectedAnswer.value = ''
    textAnswer.value = ''
    isCorrect.value = false
    hintShown.value = false
    persist()
  } else {
    nextExercise()
  }
}

async function nextExercise() {
  if (currentIndex.value < exercises.value.length - 1) {
    currentIndex.value++
    answered.value = false
    selectedAnswer.value = ''
    textAnswer.value = ''
    isCorrect.value = false
    hintShown.value = false
    persist()
  } else {
    sessionComplete.value = true
    clearSession(storageKey)
    // Demo funnel: someone who finishes all the exercises is a warm lead, so
    // record completion (and how well they did) for the landing → signup funnel.
    if (props.demoMode) {
      track('demo_finished', { score: score.value, total: exercises.value.length })
    }
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    if (props.unitId) {
      try {
        sessionResult.value = await apiPost('/api/v1/progress/record_session', {
          unit_id: props.unitId,
          exercise_type: exerciseType.value,
          attempts: sessionAttempts.value,
          timezone,
        })
      } catch (e) {
        // Was console.warn only: the learner saw "Session Complete" and their
        // score while XP, streak and unit completion were silently discarded.
        console.warn('Failed to record session progress:', e)
        saveWarning.value = true
      }
    } else if (!props.demoMode && sessionAttempts.value.length) {
      // Level checkpoints and mistakes replays: graded + XP-earning too.
      try {
        sessionResult.value = await apiPost('/api/v1/progress/record_review', {
          level: props.reviewMode ? props.level : null,
          exercise_type: exerciseType.value,
          attempts: sessionAttempts.value,
          timezone,
        })
      } catch (e) {
        // Was console.warn only: the learner saw "Session Complete" and their
        // score while XP, streak and unit completion were silently discarded.
        console.warn('Failed to record review session:', e)
        saveWarning.value = true
      }
    }
  }
}

function resetSession() {
  exerciseType.value = null
  exercises.value = []
  currentIndex.value = 0
  score.value = 0
  answered.value = false
  selectedAnswer.value = ''
  textAnswer.value = ''
  isCorrect.value = false
  hintShown.value = false
  selectedWords.value = []
  availableWords.value = []
  sessionComplete.value = false
  sessionAttempts.value = []
  sessionResult.value = null
  error.value = null
  clearSession(storageKey)
  // The demo and mistakes modes have no type picker — jump straight back in.
  if (props.demoMode || props.mistakesMode) startExercises('mixed')
}

// --- Keyboard: 1-4 answers options, Enter advances ---
function onKeydown(e) {
  if (!exercises.value.length || sessionComplete.value || generating.value) return
  const tag = document.activeElement?.tagName
  const typing = tag === 'INPUT' || tag === 'TEXTAREA'

  if (e.key === 'Enter') {
    // A focused button handles Enter natively (don't double-fire Next).
    if (tag === 'BUTTON') return
    if (answered.value) {
      e.preventDefault()
      nextExercise()
    } else if (!typing && renderType.value === 'sentence_building' && selectedWords.value.length) {
      e.preventDefault()
      submitAnswer(selectedWords.value.join(' '))
    }
    return
  }

  if (typing || answered.value) return
  const n = parseInt(e.key, 10)
  if (Number.isNaN(n) || n < 1) return
  const ex = currentExercise.value
  if (renderType.value === 'tone_drill' && ex.audio_options) {
    if (n <= ex.audio_options.length) submitAnswer(ex.audio_options[n - 1])
  } else if (ex.options && ['multiple_choice', 'listening', 'tone_drill'].includes(renderType.value)) {
    if (n <= ex.options.length) submitAnswer(ex.options[n - 1].text)
  }
}

onMounted(() => {
  window.addEventListener('keydown', onKeydown)

  savedState = loadSession(storageKey)
  if (savedState) resumeAvailable.value = true

  // Demo visitors skip the type-selection grid entirely; mistakes mode
  // auto-starts unless there's a session to resume.
  if (props.demoMode) startExercises('mixed')
  else if (props.mistakesMode && !savedState) startExercises('mixed')
})

onUnmounted(() => {
  window.removeEventListener('keydown', onKeydown)
})
</script>
